import { useEffect, useState } from 'preact/hooks';
import { type AuditEvent, type AuditIntegrityReport, DashboardApi, HttpError } from '../api.js';
import type { AuthOrg } from '../auth.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

function formatTs(iso: string): string {
  const d = new Date(iso);
  const ageMs = Date.now() - d.getTime();
  if (ageMs < 60_000) return 'just now';
  if (ageMs < 3_600_000) return `${Math.floor(ageMs / 60_000)}m ago`;
  if (ageMs < 86_400_000) return `${Math.floor(ageMs / 3_600_000)}h ago`;
  return d.toLocaleString();
}

function summarize(e: AuditEvent): string {
  const d = e.details;
  if (typeof d['summary'] === 'string') return d['summary'];
  if (typeof d['outcome'] === 'string') return d['outcome'];
  if (typeof d['action'] === 'string') return `action: ${d['action']}`;
  return '';
}

function host(e: AuditEvent): string {
  const url =
    (e.details as { pageUrl?: string; url?: string }).pageUrl ??
    (e.details as { url?: string }).url ??
    '';
  if (!url) return '—';
  try {
    return new URL(url).hostname + new URL(url).pathname;
  } catch {
    return url;
  }
}

const isoOrUndef = (v: string): string | undefined => {
  if (!v) return undefined;
  // Treat YYYY-MM-DD as start-of-day UTC; otherwise pass through.
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return `${v}T00:00:00.000Z`;
  return v;
};

export function Audit({ org }: { org: AuthOrg }): preact.ComponentChildren {
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState('');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [loading, setLoading] = useState(false);
  const [integrity, setIntegrity] = useState<AuditIntegrityReport | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [integrityError, setIntegrityError] = useState<string | null>(null);

  const verifyChain = async () => {
    setVerifying(true);
    setIntegrityError(null);
    try {
      const r = await api.verifyAuditIntegrity(org.id);
      setIntegrity(r);
    } catch (e) {
      setIntegrityError(e instanceof HttpError ? e.message : String(e));
      setIntegrity(null);
    } finally {
      setVerifying(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const filter: { kind?: string; since?: string; until?: string; limit?: number } = {
        limit: 500,
      };
      if (kind.trim()) filter.kind = kind.trim();
      const s = isoOrUndef(since.trim());
      if (s) filter.since = s;
      const u = isoOrUndef(until.trim());
      if (u) filter.until = u;
      const r = await api.listAudit(org.id, filter);
      setEvents(r.events);
      setError(null);
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh closes over filter state intentionally
  useEffect(() => {
    void refresh();
  }, [org.id]);

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify({ orgId: org.id, events }, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wholisphere-audit-${org.slug}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const csvFilter: { kind?: string; since?: string; until?: string; limit?: number } = {
    limit: 2000,
  };
  if (kind.trim()) csvFilter.kind = kind.trim();
  const csvSince = isoOrUndef(since.trim());
  if (csvSince) csvFilter.since = csvSince;
  const csvUntil = isoOrUndef(until.trim());
  if (csvUntil) csvFilter.until = csvUntil;
  const csvHref = api.auditCsvUrl(org.id, csvFilter);

  return (
    <>
      <div class="page-header">
        <h1>Audit log</h1>
        <p>
          Every agent action recorded against {org.name}. Court-defensible. The agent + GitHub
          Action + dashboard all write here; export anytime as JSON or CSV for compliance review or
          SIEM ingest.
        </p>
      </div>

      {error && <Banner variant="warn">{error}</Banner>}

      <div class="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0 }}>Hash-chain integrity</h2>
            <p class="small muted" style={{ margin: '4px 0 0' }}>
              Every audit row is hashed and chained to its predecessor. This walks the chain and
              reports any tampering. Read-only — safe to run anytime.
            </p>
          </div>
          <button type="button" disabled={verifying} onClick={() => void verifyChain()}>
            {verifying ? 'Verifying…' : 'Verify chain'}
          </button>
        </div>
        {integrityError && (
          <Banner variant="warn" style={{ marginTop: 12 }}>
            {integrityError}
          </Banner>
        )}
        {integrity && (
          <div style={{ marginTop: 12 }}>
            <Banner
              variant={integrity.ok ? 'success' : 'warn'}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <strong>
                {integrity.ok
                  ? `✓ Chain intact — ${integrity.verified.toLocaleString()} row${integrity.verified === 1 ? '' : 's'} verified`
                  : `✗ Tampering detected — ${integrity.findings.length} finding${integrity.findings.length === 1 ? '' : 's'}`}
              </strong>
              {integrity.truncated && (
                <span class="small muted">⚠ truncated at hard cap (100k rows)</span>
              )}
            </Banner>
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'max-content 1fr',
                columnGap: 16,
                rowGap: 4,
                marginTop: 12,
              }}
            >
              <dt class="small muted">Total rows</dt>
              <dd>{integrity.totalRows.toLocaleString()}</dd>
              <dt class="small muted">Verified</dt>
              <dd>{integrity.verified.toLocaleString()}</dd>
              <dt class="small muted">Pre-chain (legacy)</dt>
              <dd>{integrity.preChain.toLocaleString()}</dd>
              <dt class="small muted">Chain restarts</dt>
              <dd>{integrity.restarts}</dd>
              <dt class="small muted">Latest hash</dt>
              <dd>
                <code title={integrity.latestHash}>
                  {integrity.latestHash ? `${integrity.latestHash.slice(0, 16)}…` : '—'}
                </code>
              </dd>
              <dt class="small muted">Chain version</dt>
              <dd>v{integrity.chainVersion}</dd>
            </dl>
            {integrity.findings.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <h3 style={{ fontSize: 14, margin: '8px 0' }}>Findings</h3>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {integrity.findings.slice(0, 20).map((f) => (
                    <li key={f.rowId} class="small">
                      <span class="pill pill-fails">{f.kind}</span>{' '}
                      <code>{f.rowId.slice(0, 12)}…</code>{' '}
                      <span class="muted">{new Date(f.ts).toLocaleString()}</span>
                      <br />
                      <span>{f.message}</span>
                    </li>
                  ))}
                  {integrity.findings.length > 20 && (
                    <li class="small muted">
                      …and {integrity.findings.length - 20} more. Export the audit log to inspect
                      them all.
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <div class="card">
        <h2>Filter</h2>
        <div class="field">
          <label htmlFor="audit-kind">Kind</label>
          <input
            id="audit-kind"
            type="text"
            placeholder="e.g. capability.invoked"
            value={kind}
            onInput={(e) => setKind((e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="field" style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="audit-since">Since</label>
            <input
              id="audit-since"
              type="date"
              value={since}
              onInput={(e) => setSince((e.target as HTMLInputElement).value)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="audit-until">Until</label>
            <input
              id="audit-until"
              type="date"
              value={until}
              onInput={(e) => setUntil((e.target as HTMLInputElement).value)}
            />
          </div>
        </div>
        <button type="button" disabled={loading} onClick={() => void refresh()}>
          {loading ? 'Searching…' : 'Apply filter'}
        </button>
        <button
          type="button"
          class="secondary"
          style={{ marginLeft: 8 }}
          onClick={() => {
            setKind('');
            setSince('');
            setUntil('');
            setTimeout(() => void refresh(), 0);
          }}
        >
          Clear
        </button>
      </div>

      <div class="card">
        <h2>Activity</h2>
        {events === null ? (
          <p class="muted">
            <output>Loading…</output>
          </p>
        ) : events.length === 0 ? (
          <div class="empty">
            <p>No events for this filter.</p>
            <p class="small muted">
              Install the embed script or wire up the GitHub Action — events stream here as they
              happen.
            </p>
          </div>
        ) : (
          <table class="audit">
            <thead>
              <tr>
                <th>When</th>
                <th>Where</th>
                <th>Kind</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id}>
                  <td>{formatTs(e.ts)}</td>
                  <td>
                    <code class="meta">{host(e)}</code>
                  </td>
                  <td>
                    <span class="kind-pill">{e.kind}</span>
                  </td>
                  <td>{summarize(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div class="card">
        <h2>Export</h2>
        <p class="desc">
          JSON for ad-hoc review; CSV for SIEM ingest (Splunk / Datadog) or spreadsheets.
        </p>
        <button
          type="button"
          class="secondary"
          disabled={!events || events.length === 0}
          onClick={downloadJson}
        >
          Download JSON
        </button>
        <a class="btn-link secondary" style={{ marginLeft: 8 }} href={csvHref}>
          Download CSV
        </a>
      </div>
    </>
  );
}
